import {
  CardContents,
  ManualCardContents,
  Shop,
  VEHICLE_FIELDS,
} from "@/types/v2";
import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/Base/Form";
import { z, ZodObject, ZodRawShape } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Switch } from "@/components/Base/Switch";
import { MANUAL_CARD_CONFIG_OPTIONS } from "@/lib/const";
import { useTerminologyReplacer } from "@/hooks/useTerminologyReplacer";
import { VehicleFieldSelector } from "./VehicleFieldSelector";

const shape: ZodRawShape = {
  jobHoursEnabled: z.boolean().optional(),
  promiseTimeEnabled: z.boolean().optional(),
  technicianEnabled: z.boolean().optional(),
  serviceAdvisorEnabled: z.boolean().optional(),
  manualMarkCompleteEnabled: z.boolean().optional(),
};

const formSchema: ZodObject<typeof shape> = z.object({
  ...shape,
});

const defaultValues: FormSchema = {
  ...Object.fromEntries(
    Object.entries(MANUAL_CARD_CONFIG_OPTIONS).map(([key]) => [key, false])
  ),
};

type FormSchema = Pick<CardContents, ManualCardContents>;

type ManualCardOptionsProps = {
  shop: Shop;
  onSubmit?: (values: FormSchema) => void;
};
export const ManualCardOptions = ({
  shop,
  onSubmit,
}: ManualCardOptionsProps) => {
  const { terminolizeAuto } = useTerminologyReplacer();
  const formDefaultValues: FormSchema = {
    ...defaultValues,
    ...shop.cardConfig.cardContents,
  };

  const { watch, ...form } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    if (onSubmit) {
      onSubmit(values);
    }
  };

  const handleSwitchChange = (
    fieldValue: boolean,
    fieldName: keyof FormSchema
  ) => {
    form.setValue(fieldName, fieldValue);
    handleSubmit(form.getValues());
  };

  return (
    <Form {...form} watch={watch}>
      <form
        className="w-full min-w-64 space-y-4"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <div className="flex flex-col gap-4 md:grid md:grid-cols-2">
          {Object.keys(MANUAL_CARD_CONFIG_OPTIONS).map((key) => {
            const option =
              MANUAL_CARD_CONFIG_OPTIONS[key as ManualCardContents];
            return (
              <FormField
                key={key}
                name={key}
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between gap-2 rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">
                        {terminolizeAuto(option.label, ["singular"])}
                      </FormLabel>
                      <FormDescription>
                        {terminolizeAuto(option.description, [
                          "plural",
                          "singular",
                        ])}
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={(value) =>
                          handleSwitchChange(
                            value,
                            field.name as keyof FormSchema
                          )
                        }
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            );
          })}
        </div>
        {watch("manualVehicleInfoEnabled") && (
          <div className="rounded-lg border p-4">
            <VehicleFieldSelector
              selectedFields={
                shop?.cardConfig?.cardContents?.requiredVehicleFields || [
                  ...VEHICLE_FIELDS,
                ]
              }
              onChange={(fields) => {
                const currentValues = form.getValues();
                handleSubmit({
                  ...currentValues,
                  requiredVehicleFields: fields,
                });
              }}
            />
          </div>
        )}
      </form>
    </Form>
  );
};
